import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Loader from '../components/Loader';
import Alert from '../components/Alert';
import feather from 'feather-icons';
import serverClient from '../api/ServerClient';
import SuccessToast from '../toast/SuccessToast';
import { useLanguage } from '../context/LanguageContext';
import { getPageMeta } from '../util/seoHelper';

const ResetPassword = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { language, t } = useLanguage();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showToast, setShowToast] = useState(false);

    // State for password fields
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // State to toggle password visibility
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        feather.replace();
        // // Guard route access
        // if (!location.state || !location.state.email || !location.state.token) {
        //     navigate("/login");
        // }
    }, [location, navigate]);

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
    };

    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError(t('passwordsDoNotMatch'));
            return;
        }

        setLoading(true);
        try {
            const response = await serverClient.post('/api/auth/reset-password', {
                email: location.state.email,
                otp: location.state.token,
                password,
            });

            if (response.status === 200) {
                setShowToast(true);
                setTimeout(() => navigate('/login'), 2000);
            }
        } catch (err) {
            setLoading(false);
            if (err.response?.status === 400) {
                setError(err.response?.data?.message || t('somethingWentWrong'));
            } else {
                setError(t('somethingWentWrong'));
            }
        }
    };

    return (
        <div className="auth-body">
            <Helmet>
                <title>{getPageMeta('resetPassword', language).title}</title>
                <meta
                    name="description"
                    content={getPageMeta('resetPassword', language).description}
                />
                <meta name="keywords" content={getPageMeta('resetPassword', language).keywords} />
                <link rel="canonical" href={getPageMeta('resetPassword', language).url} />
                {getPageMeta('resetPassword', language).noindex && (
                    <meta name="robots" content="noindex, follow" />
                )}
            </Helmet>
            <div className="auth-header">
                <Link to="/login">
                    <i className="back-btn" data-feather="arrow-left"></i>
                </Link>

                <img
                    className="img-fluid img"
                    src={'assets/images/authentication/3.svg'}
                    alt="Reset password"
                />

                <div className="auth-content">
                    <div>
                        <h2>{t('resetPassword')}</h2>
                        <h4 className="p-0">{t('enterNewPassword')}</h4>
                    </div>
                </div>
            </div>

            <Loader loading={loading} />

            {/* Form */}
            <form className="auth-form" onSubmit={handleSubmit}>
                <div className="custom-container">
                    <div className="form-group">
                        {error && <Alert alert="danger">{error}</Alert>}

                        <label htmlFor="password" className="form-label">
                            {t('newPassword')}
                        </label>
                        <div className="form-input position-relative">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                className="form-control"
                                id="password"
                                placeholder={t('enterNewPassword')}
                                value={password}
                                onChange={handlePasswordChange}
                            />
                            <span
                                className="password-toggle-icon"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M1 1l22 22" />
                                        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9.27-3-11-8 1.21-3.19 3.59-5.67 6.51-6.73" />
                                        <path d="M10.59 10.59a2 2 0 0 0 2.82 2.82" />
                                        <path d="M14.12 14.12A4 4 0 0 1 9.88 9.88" />
                                    </svg>
                                ) : (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                )}
                            </span>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword" className="form-label">
                            {t('confirmPassword')}
                        </label>
                        <div className="form-input position-relative">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                className="form-control"
                                id="confirmPassword"
                                placeholder={t('confirmPassword')}
                                value={confirmPassword}
                                onChange={handleConfirmPasswordChange}
                            />
                            <span
                                className="password-toggle-icon"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M1 1l22 22" />
                                        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9.27-3-11-8 1.21-3.19 3.59-5.67 6.51-6.73" />
                                        <path d="M10.59 10.59a2 2 0 0 0 2.82 2.82" />
                                        <path d="M14.12 14.12A4 4 0 0 1 9.88 9.88" />
                                    </svg>
                                ) : (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                )}
                            </span>
                        </div>
                    </div>

                    <button type="submit" className="btn theme-btn w-100">
                        {t('submit')}
                    </button>
                </div>
            </form>

            {showToast && (
                <SuccessToast message={t('passwordUpdated')} onClose={() => setShowToast(false)} />
            )}
        </div>
    );
};

export default ResetPassword;
